<template>
  <section
    class="popular-searches scrollable"
    :class="{ desktop: $isDesktop }"
    :style="{ gridTemplateColumns: `repeat(${columnCount}, 34vw)` }"
  >
    <pu-search-link
      v-for="(card, index) of popularSearches"
      :key="index"
      v-bind="card.to"
      exclude-type="newLocation"
      :category="card.to.category"
      :modify-filters="{
        categoryIds: undefined,
        facets: undefined,
        hierarchyPath: undefined,
      }"
      :style="{
        order: card.displayOrder && !$isDesktop ? card.displayOrder : 20,
      }"
      @click="$emit('popular-search-clicked')"
    >
      <div v-if="withImage" class="picture">
        <img
          v-if="card.isLCP"
          :src="card.imgUrl"
          :alt="card.label"
          width="133"
          height="110"
          fetchpriority="high"
          decoding="sync"
        />
        <img
          v-else
          :loading="isLazyImg(index) ? 'lazy' : 'eager'"
          :src="card.imgUrl"
          :alt="card.label"
          width="133"
          height="110"
        />
      </div>
      <span v-if="withImage">{{ $t(card.label) }}</span>
      <template v-else>
        {{ $t(card.label) }}
      </template>
    </pu-search-link>
    <nuxt-to-meta :nuxt2meta="head" />
  </section>
</template>

<script lang="ts">
import { Component, Prop, toNative, Vue } from '~/utility/pu-class-decorator'

const imageUrls = import.meta.glob('@/assets/popular-searches/*.jpg', {
  eager: true,
  query: '?url',
  import: 'default',
})

@Component({
  emits: ['popular-search-clicked'],
})
class PopularSearchesGlamping extends Vue {
  @Prop({ default: true })
  withImage: boolean

  @Prop({})
  popularSearches: {
    to: { hierarchyPath: string; category: string }
    label: string
    imgUrl: string
    isLCP: boolean
    displayOrder: Number
  }[]

  get head() {
    if (!this.withImage) {
      return {}
    }
    return {
      link: [
        {
          rel: 'preload',
          as: 'image',
          href: imageUrls['/assets/popular-searches/wales.jpg'],
          fetchpriority: 'high',
        },
      ],
    }
  }

  get columnCount() {
    return Math.round(this.popularSearches.length / 2)
  }

  isLazyImg(index: number): boolean {
    return index > 5
  }
}

export default toNative(PopularSearchesGlamping)
</script>

<style scoped>
.popular-searches {
  column-gap: 0.7em;
  display: grid;
  grid-auto-rows: minmax(28vw, auto);
  row-gap: 0.7em;
  opacity: 1;
}

a {
  box-sizing: content-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow: hidden;
  scroll-snap-align: start;
  scroll-snap-stop: normal;
  text-decoration: none;
  position: relative;
}

a span {
  font-size: var(--body);
  color: white;
  position: absolute;
  bottom: 0.7em;
  left: 0.7em;
  display: block;
  padding-right: 1.5em;
  font-weight: bold;
  line-height: 1.15;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.75);
}

.picture {
  overflow: hidden;
  height: 28vw;
  border-radius: 0.65em;
  background-color: var(--colour-page-background);
  position: relative;
}

.picture img {
  width: auto;
  height: 100%;
  max-width: none;
  aspect-ratio: auto 4/3;
}

.picture:before {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0) 50%
  );
}

/* desktop & tablet */
.popular-searches.desktop {
  grid-template-columns: repeat(8, 14em) !important;
  column-gap: 3em;
  grid-auto-rows: 12em;
  row-gap: 3em;
  margin: 0;
}

.desktop a {
  transition:
    box-shadow 0.15s ease-out,
    transform 0.25s ease-out;
  border-radius: 1em;
}

.desktop a:hover,
.desktop a:focus-visible {
  box-shadow: 0 0 0 7px var(--focus-ring-colour);
  transform: scale(1.05);
  outline: none;
}

.desktop a span {
  font-size: 1.3em;
}

.desktop .picture {
  height: 100%;
}
</style>
